import { css, CSSProperties } from 'linaria';
import { styled } from 'linaria/react';
import { FunctionalComponent, h } from 'preact';

import { cn } from 'libs/classnames';
import { SpinnerIcon } from 'ui/icons/spinner-icon';

type ButtonType = 'primary' | 'outline' | 'default' | 'link';
type ButtonProps = {
	type?: ButtonType;
	onClick?: (e: MouseEvent) => void
	className?: string;
	icon?: any;
	disabled?: boolean;
	loading?: boolean;
	style?: CSSProperties;
	name?: string
}

export const Button: FunctionalComponent<ButtonProps> = (props) => {
	const { type = 'primary', children, onClick, className, icon, disabled, loading, style } = props;
	let isDisabled = disabled;

	if (loading) {
		isDisabled = true;
	}

	return (
		<button
			style={style as any}
			disabled={isDisabled}
			onClick={onClick}
			className={cn(
				base,
				{
					[btn]: ['primary', 'outline', 'link'].includes(type),
					[primary]: type === 'primary',
					[outline]: type === 'outline',
					[link]: type === 'link',
					[disabledClass]: isDisabled,
				},
				className,
			)}
		>
			{loading && (
				<IconContainer>
					<SpinnerIcon className={spinner}/>
				</IconContainer>
			)}
			{icon && !loading && (
				<IconContainer>
					{icon}
				</IconContainer>
			)}
			<span>
				{children}
			</span>
		</button>
	);
};

const base = css`
	padding: 8px;
	margin: 0;
	border: none;
	background-color: transparent;
	outline: none;
	cursor: pointer;
	transition: .2s ease-in-out;
	
	span {
		display: flex;
	}
`;
const btn = css`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 40px;
	border-radius: 4px;
	padding: 0 40px;
	border: 1px solid;
	
	span {
		display: flex;
		align-items: center;
		font-style: normal;
		font-weight: 600;
		line-height: 24px;
		font-size: 16px;
	}
	
	@media (max-width: 560px) {
		min-height: 50px;
		border-radius: 8px;
	}
`;
const outline = css`
	color: var(--button-outline-text);
	background: transparent;
	border-color: var(--button-outline-border);
	
	&:hover {
		background-color: transparent;
		border-color: var(--button-outline-border);
	}
`;
const primary = css`
	color: var(--button-primary-text);
	background: var(--primary-color);
	border-color: var(--primary-color);
	
	&:hover:not([disabled]) {
		background-color: var(--button-primary-hover);
		border-color: (--button-primary-hover);
	}
`;
const link = css`
	color: var(--primary-color);
	border: transparent;
	
	span {
		font-weight: 400;
	}
`;
const disabledClass = css`
	opacity: .4;
	cursor: default;
	
	&:hover:not([disabled]) {
		background-color: initial;
		border-color: initial;
	}
`;
const IconContainer = styled('div')`
	margin-right: 8px;
`;
const spinner = css`
   	animation: spin 1s ease-in-out infinite;

	@keyframes spin {
	    from {
	        transform:rotate(0deg);
	    }
	    to {
	        transform:rotate(360deg);
	    }
	}
`;
